// Apps → Update Batches client. Every status here is derived server-side
// from builds, QA verdicts and the Play review poll (see
// api/cloudflare/src/lib/update-batches.ts) — nothing is edited by hand.
import { api } from './client';

const BASE = '/admin/apps-setup/update-batches';

export type BatchStatus = 'active' | 'completed' | 'archived';
export type QaVerdict = 'approved' | 'rejected' | 'not_checked';
export type PublishState = 'published' | 'in_review' | 'not_published' | 'no_play';

export interface BatchProgress {
  total: number; apk_built: number; qa_approved: number; qa_rejected: number; aab_built: number;
  /** Live on Play, and — apart from those — sent to Play review, not live yet. */
  published: number; in_review: number;
  done: number;
}

export interface UpdateBatch {
  id: number; name: string; status: BatchStatus;
  created_at: number; closed_at: number | null;
  created_by: string | null; created_by_name: string | null; created_by_email: string | null;
  bundle_count: number;
  repos: { id: number; name: string }[];
  progress: BatchProgress;
}

export interface BuildRef { build_id: string; version_name: string; version_code: number; built_at: number; }

export interface BatchApp {
  app_id: string; name: string; bundle_id: string; icon_url: string | null;
  repo_name: string | null; added_at: number;
  play_app_id: number | null; play_account_id: number | null;
  apk: BuildRef | null;
  qa: { verdict: QaVerdict; note: string | null };
  aab: BuildRef | null;
  published: { state: PublishState; since: string | null; version_code: number | null };
  whats_new: { added: boolean; langs: string[] };
  in_flight: { apk: boolean; aab: boolean };
  // Build tab readiness; `missing` names what still has to be set up.
  build_ready: { ok: boolean; missing: string[] };
  done: boolean;
}

export interface UpdateBatchDetail extends UpdateBatch { apps: BatchApp[]; }

export interface Candidate {
  id: string; name: string; bundle_id: string; icon_url: string | null; repo_name: string | null;
  external_account: boolean;
  publisher_name: string | null;
  busy_batch: { id: number; name: string } | null;
}

export interface SkippedApp {
  app_id: string; name: string; bundle_id: string;
  reason: 'busy' | 'already_in_batch' | 'not_found';
  batch?: { id: number; name: string };
}
export interface AddResult { added: string[]; skipped: SkippedApp[]; }

export const listUpdateBatches = (params: { status?: BatchStatus | ''; q?: string } = {}) => {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  const s = qs.toString();
  return api.get<UpdateBatch[]>(`${BASE}${s ? `?${s}` : ''}`);
};

export const getUpdateBatch = (id: number) => api.get<UpdateBatchDetail>(`${BASE}/${id}`);

export const createUpdateBatch = (body: { name: string; app_ids: string[] }) =>
  api.post<{ batch: UpdateBatch } & AddResult>(BASE, body);

export const addBatchApps = (id: number, app_ids: string[]) =>
  api.post<AddResult>(`${BASE}/${id}/apps`, { app_ids });

export const removeBatchApp = (id: number, appId: string) =>
  api.delete<null>(`${BASE}/${id}/apps/${encodeURIComponent(appId)}`);

/** Takes a pinned APK/AAB off the batch; the build itself stays in the Build tab history. */
export const dismissBatchBuild = (id: number, buildId: string) =>
  api.delete<null>(`${BASE}/${id}/builds/${encodeURIComponent(buildId)}`);

export const archiveUpdateBatch = (id: number) => api.post<null>(`${BASE}/${id}/archive`, {});

export const deleteUpdateBatch = (id: number) => api.delete<null>(`${BASE}/${id}`);

export type CandidateSource = (
  | { source: 'repo'; repo_id: number }
  | { source: 'published' }
  | { source: 'search'; q: string }
) & { ours_only?: boolean; published_only?: boolean };

export const listCandidates = (src: CandidateSource) => {
  const qs = new URLSearchParams({ source: src.source });
  if (src.source === 'repo') qs.set('repo_id', String(src.repo_id));
  if (src.source === 'search') qs.set('q', src.q);
  if (src.ours_only) qs.set('ours_only', '1');
  if (src.published_only && src.source !== 'published') qs.set('published_only', '1');
  return api.get<Candidate[]>(`${BASE}/candidates?${qs.toString()}`);
};
